import React, { useState } from 'react';
import {
  CreateTimedMessageInput,
  validateTimedMessage,
  RUMBLE_MESSAGE_LIMITS,
  getTimezoneOptions,
  getDayOptions
} from '../lib/timedMessages';
import { supabase } from '../lib/supabase';

interface TimedMessageFormProps {
  onSubmit: (message: CreateTimedMessageInput) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  initialData?: Partial<CreateTimedMessageInput>;
}

const TimedMessageForm: React.FC<TimedMessageFormProps> = ({
  onSubmit,
  onCancel,
  loading = false,
  initialData
}) => {
  const [formData, setFormData] = useState<CreateTimedMessageInput>({
    message: initialData?.message || '',
    schedule_type: initialData?.schedule_type || 'interval',
    interval_minutes: initialData?.interval_minutes || 5,
    specific_time: initialData?.specific_time || '12:00',
    specific_timezone: initialData?.specific_timezone || 'UTC',
    specific_days: initialData?.specific_days || [1, 2, 3, 4, 5, 6, 7],
    enabled: initialData?.enabled !== false,
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [mentionWord, setMentionWord] = useState<string>('dork');

  // Fetch mention_word on component mount
  React.useEffect(() => {
    const fetchMentionWord = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: rumbleSettings } = await supabase
            .from('user_rumble_settings')
            .select('mention_word')
            .eq('user_id', user.id)
            .single();

          if (rumbleSettings?.mention_word) {
            setMentionWord(rumbleSettings.mention_word);
          }
        }
      } catch (error) {
        console.error('Failed to fetch mention word:', error);
      }
    };

    fetchMentionWord();
  }, []);

  // Update form data when initialData changes (for editing)
  React.useEffect(() => {
    if (initialData) {
      setFormData({
        message: initialData.message || '',
        schedule_type: initialData.schedule_type || 'interval',
        interval_minutes: initialData.interval_minutes || 5,
        specific_time: initialData.specific_time || '12:00',
        specific_timezone: initialData.specific_timezone || 'UTC',
        specific_days: initialData.specific_days || [1, 2, 3, 4, 5, 6, 7],
        enabled: initialData.enabled !== false,
      });
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateTimedMessage(formData, mentionWord);
    setErrors(validationErrors);
    
    if (validationErrors.length > 0) {
      return;
    }
    
    try {
      await onSubmit(formData);
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Failed to save message']);
    }
  };

  const handleDayToggle = (dayValue: number) => {
    const currentDays = formData.specific_days || [];
    const newDays = currentDays.includes(dayValue)
      ? currentDays.filter(d => d !== dayValue)
      : [...currentDays, dayValue].sort();
    
    setFormData({ ...formData, specific_days: newDays });
  };

  const timezoneOptions = getTimezoneOptions();
  const dayOptions = getDayOptions();
  const messageLength = (formData.message || '').length;
  const isMessageTooLong = messageLength > RUMBLE_MESSAGE_LIMITS.MAX_LENGTH;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-black border-2 border-white p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-white">
            {initialData ? 'Edit Timed Message' : 'Add Timed Message'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition-colors"
            disabled={loading}
          >
            <i className="fa-solid fa-times text-xl"></i>
          </button>
        </div>

        {errors.length > 0 && (
          <div className="mb-4 p-3 border-2 border-red-500 text-red-400">
            <ul className="list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Message Content */}
          <div>
            <label htmlFor="message" className="paint-label">
              Message Content *
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className={`paint-input h-24 resize-none ${isMessageTooLong ? 'border-red-500' : ''}`}
              placeholder="Enter your message..."
              maxLength={RUMBLE_MESSAGE_LIMITS.MAX_LENGTH + 50} // Allow typing a bit over to show error
              disabled={loading}
            />
            <div className="flex justify-between text-xs mt-1">
              <span className={isMessageTooLong ? 'text-red-400' : 'text-gray-400'}>
                {messageLength}/{RUMBLE_MESSAGE_LIMITS.MAX_LENGTH} characters
              </span>
              {isMessageTooLong && (
                <span className="text-red-400">Message too long</span>
              )}
            </div>
          </div>

          {/* Schedule Type */}
          <div>
            <label className="paint-label">Schedule Type *</label>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="schedule_type"
                  value="interval"
                  checked={formData.schedule_type === 'interval'}
                  onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value as 'interval' })}
                  className="text-white"
                  disabled={loading}
                />
                <span className="text-white">Every X minutes</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="schedule_type"
                  value="specific_time"
                  checked={formData.schedule_type === 'specific_time'}
                  onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value as 'specific_time' })}
                  className="text-white"
                  disabled={loading}
                />
                <span className="text-white">Specific time</span>
              </label>
            </div>
          </div>

          {/* Interval Settings */}
          {formData.schedule_type === 'interval' && (
            <div>
              <label htmlFor="interval_minutes" className="paint-label">
                Interval (minutes) *
              </label>
              <input
                type="number"
                id="interval_minutes"
                value={formData.interval_minutes || ''}
                onChange={(e) => setFormData({ ...formData, interval_minutes: parseInt(e.target.value) || undefined })}
                className="paint-input w-32"
                min={RUMBLE_MESSAGE_LIMITS.MIN_INTERVAL_MINUTES}
                max={RUMBLE_MESSAGE_LIMITS.MAX_INTERVAL_MINUTES}
                placeholder="5"
                disabled={loading}
              />
              <p className="text-xs text-gray-400 mt-1">
                Between {RUMBLE_MESSAGE_LIMITS.MIN_INTERVAL_MINUTES} and {RUMBLE_MESSAGE_LIMITS.MAX_INTERVAL_MINUTES} minutes
              </p>
            </div>
          )}

          {/* Specific Time Settings */}
          {formData.schedule_type === 'specific_time' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="specific_time" className="paint-label">
                    Time *
                  </label>
                  <input
                    type="time"
                    id="specific_time"
                    value={formData.specific_time || ''}
                    onChange={(e) => setFormData({ ...formData, specific_time: e.target.value })}
                    className="paint-input"
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label htmlFor="specific_timezone" className="paint-label">
                    Timezone
                  </label>
                  <select
                    id="specific_timezone"
                    value={formData.specific_timezone || 'UTC'}
                    onChange={(e) => setFormData({ ...formData, specific_timezone: e.target.value })}
                    className="paint-input"
                    disabled={loading}
                  >
                    {timezoneOptions.map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="paint-label">Days of Week *</label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {dayOptions.map(day => (
                    <label key={day.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(formData.specific_days || []).includes(day.value)}
                        onChange={() => handleDayToggle(day.value)}
                        className="text-white"
                        disabled={loading}
                      />
                      <span className="text-white text-sm">{day.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Enabled Toggle */}
          <div>
            <label className="paint-label inline-flex items-center justify-between">
              <span>Enable message:</span>
              <label className="toggle-switch ml-4">
                <input 
                  type="checkbox" 
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  disabled={loading}
                />
                <span className="toggle-slider"></span>
              </label>
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="paint-button paint-button-sm bg-gray-700 hover:bg-gray-600"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="paint-button paint-button-sm"
              disabled={loading || isMessageTooLong}
            >
              {loading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-save mr-2"></i>
                  {initialData ? 'Update Message' : 'Add Message'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimedMessageForm;